<template>
  <div class="ss-page signup-links-section">
    <!-- Create Source Card -->
    <div class="ss-card">
      <h2 class="ss-card__title"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" style="vertical-align: -2px; margin-right: 5px;"><path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z" fill="#0C2454"/></svg>Create a source link</h2>
      <p class="ss-card__desc">Generate a trackable link to share via social, email, or offline channels.</p>
      <div style="height: 14px;"></div>

      <div class="ss-form">
        <div class="ss-form__grid">
          <div class="ss-form__field">
            <label class="ss-form__label">Agent</label>
            <select v-model="newSource.agentId" class="ss-input ss-select">
              <option value="" disabled selected>Select agent</option>
              <option v-for="a in agents" :key="a.id" :value="a.id">{{ a.label }}</option>
            </select>
          </div>
          <div class="ss-form__field">
            <label class="ss-form__label">Source</label>
            <select v-if="newSource.source !== '__custom__'" v-model="newSource.source" class="ss-input ss-select">
              <option value="" disabled>Select source</option>
              <option v-for="s in sourceOptions" :key="s" :value="s">{{ s }}</option>
              <option value="__custom__">Custom...</option>
            </select>
            <div v-else class="ss-custom-source">
              <input
                v-model="newSource.customSource"
                placeholder="Type custom source"
                class="ss-input ss-custom-source__input"
                ref="customSourceInput"
              />
              <button class="ss-custom-source__cancel" @click="newSource.source = ''" title="Back to list">&times;</button>
            </div>
          </div>
          <div class="ss-form__field">
            <label class="ss-form__label">Campaign <span class="ss-form__optional">(optional)</span></label>
            <input
              v-model="newSource.campaign"
              placeholder="e.g. Spring 2026"
              class="ss-input"
            />
          </div>
          <div class="ss-form__field ss-form__field--btn">
            <button
              class="ss-generate-btn"
              :class="{ 'ss-generate-btn--disabled': !newSource.agentId || (!newSource.source && !newSource.customSource), 'ss-generate-btn--loading': creating }"
              :disabled="!newSource.agentId || (!newSource.source && !newSource.customSource) || creating"
              @click="createSource"
            >
              <v-progress-circular v-if="creating" indeterminate size="16" width="2" color="white" style="margin-right: 6px;"></v-progress-circular>
              Generate Link
            </button>
          </div>
        </div>
      </div>

      <!-- Generated result (compact row) -->
      <div v-if="generatedLink" class="ss-result-row">
        <div class="ss-result-row__left">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style="flex-shrink: 0;"><path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z" fill="#2F6FED"/></svg>
          <span class="ss-result-row__url">{{ generatedLink }}</span>
          <span style="font-size: 11px; color: #98A2B3; white-space: nowrap; font-style: italic;">(Test this link before using)</span>
        </div>
        <div class="ss-result-row__right">
          <div class="ss-result-row__qr">
            <qrcode-vue :value="generatedLink" :size="40" level="M" id="source-qr" />
          </div>
          <button class="ss-result-row__dl" @click="downloadQR(generatedLink, 'source-qr')" title="Download QR">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" fill="#667085"/></svg>
          </button>
          <button class="ss-result-row__copy" @click="copyLink">
            {{ copied ? 'Copied' : 'Copy' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Source Links Table Card -->
    <div class="ss-card">
      <div class="ss-table-header">
        <div class="ss-table-header__left">
          <h2 class="ss-card__title" style="margin: 0;">All Source Links</h2>
          <span class="ss-badge">{{ sources.length }}</span>
        </div>
        <div class="ss-table-header__search">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style="position: absolute; left: 10px; top: 50%; transform: translateY(-50%);"><path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="#98A2B3"/></svg>
          <input v-model="search" placeholder="Search links..." class="ss-search-input" />
        </div>
      </div>

      <v-data-table
        :headers="headers"
        :items="sources"
        :search="search"
        :loading="loading"
        class="ss-data-table"
        :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
        :items-per-page="25"
        sort-by="created_at"
        sort-desc
        :expanded.sync="expanded"
        show-expand
        item-key="id"
      >
        <template v-slot:item.agent_name="{ item }">
          <span class="ss-cell-bold">{{ item.agent_name || 'N/A' }}</span>
        </template>

        <template v-slot:item.source_campaign="{ item }">
          <div>
            <span class="ss-cell-bold" style="text-transform: capitalize;">{{ item.source }}</span>
            <span v-if="item.campaign" class="ss-cell-muted" style="display: block; margin-top: 1px;">{{ item.campaign }}</span>
          </div>
        </template>

        <template v-slot:item.url="{ item }">
          <div style="display: flex; align-items: center; gap: 6px;">
            <a :href="item.url" target="_blank" class="ss-cell-link">{{ item.url }}</a>
            <button class="ss-icon-btn" @click="copyText(item.url)" title="Copy link">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" fill="#667085"/></svg>
            </button>
          </div>
        </template>

        <template v-slot:item.qr="{ item }">
          <div class="ss-qr-cell" @click="downloadQR(item.url, 'qr-' + item.id)" title="Download QR">
            <qrcode-vue :value="item.url" :size="48" level="M" :id="'qr-' + item.id" />
          </div>
        </template>

        <template v-slot:item.open_count="{ item }">
          <span class="ss-signups" :class="(item.open_count || 0) > 0 ? 'ss-signups--active' : ''">{{ item.open_count || 0 }}</span>
        </template>

        <template v-slot:item.signup_count="{ item }">
          <span class="ss-signups" :class="item.signup_count > 0 ? 'ss-signups--active' : ''">{{ item.signup_count }}</span>
        </template>

        <template v-slot:item.reach="{ item }">
          <input type="number" min="0" v-model.number="item.reach" placeholder="0" class="ss-reach-input" @blur="updateReach(item)" @keyup.enter="updateReach(item); $event.target.blur()" />
        </template>

        <template v-slot:item.conversion="{ item }">
          <span v-if="item.reach > 0" class="ss-conversion" :style="{ color: conversionColor(item.signup_count, item.reach) }">
            {{ ((item.signup_count / item.reach) * 100).toFixed(1) }}%
          </span>
          <span v-else class="ss-cell-muted">0%</span>
        </template>

        <template v-slot:item.created_at="{ item }">
          <span class="ss-cell-muted" style="font-size: 12px;">{{ formatDate(item.created_at) }}</span>
        </template>

        <template v-slot:expanded-item="{ headers: h, item }">
          <td :colspan="h.length" class="ss-expanded">
            <div v-if="item.signups && item.signups.length">
              <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px;">
                <p class="ss-expanded__title" style="margin: 0;">Signups from this source ({{ item.signups.length }})</p>
                <button class="ss-download-btn" @click="downloadSignups(item)">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style="margin-right: 4px;"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" fill="currentColor"/></svg>
                  Export Excel
                </button>
              </div>
              <table class="ss-expanded__table">
                <thead>
                  <tr><th>Name</th><th>Role</th><th>Phone</th><th>Email</th><th>Status</th><th>Date</th></tr>
                </thead>
                <tbody>
                  <tr v-for="s in item.signups" :key="s.id">
                    <td>{{ s.first_name }} {{ s.last_name }}</td>
                    <td style="text-transform: capitalize;">{{ s.role }}</td>
                    <td>{{ s.phone_number }}</td>
                    <td>{{ s.email }}</td>
                    <td style="text-transform: capitalize;">{{ s.status }}</td>
                    <td>{{ formatDate(s.created_at) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="ss-expanded__empty">No signups from this source yet.</div>
          </td>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import config from "@/config/constants.js";
import QrcodeVue from "qrcode.vue";
import { listTenantAgents, authHeaders } from "./marketing_api";

export default {
  name: "SignupLinksSection",
  components: { QrcodeVue },
  props: {
    // When set (super-user Marketing Console), agent + source calls act on this
    // tenant instead of the caller's own. Defaults preserve current behavior.
    tenantId: { type: String, default: "" },
    superAdmin: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      creating: false,
      sources: [],
      agents: [],
      search: "",
      expanded: [],
      generatedLink: "",
      copied: false,
      newSource: {
        agentId: "",
        source: "",
        customSource: "",
        campaign: "",
      },
      sourceOptions: [
        'Facebook', 'Instagram', 'Twitter', 'LinkedIn',
        'Email', 'SMS', 'Flyer', 'Postcard', 'Google', 'Other',
      ],
      headers: [
        { text: "", value: "data-table-expand", width: '40px' },
        { text: "Agent", value: "agent_name", sortable: true },
        { text: "Source", value: "source_campaign", sortable: true },
        { text: "Link", value: "url", sortable: false },
        { text: "QR", value: "qr", sortable: false, width: '64px' },
        { text: "Opens", value: "open_count", sortable: true, width: '80px', align: 'center' },
        { text: "Signups", value: "signup_count", sortable: true, width: '80px', align: 'center' },
        { text: "Reach", value: "reach", sortable: true, width: '100px' },
        { text: "Conv.", value: "conversion", sortable: true, width: '70px', align: 'center' },
        { text: "Created", value: "created_at", sortable: true, width: '100px' },
      ],
    };
  },
  mounted() {
    this.fetchAgents();
    this.fetchSources();
  },
  methods: {
    async fetchAgents() {
      // Backed by GET /v1/marketing/tenant-agents — returns { id, label,
      // user_role } already shaped for the <select>. Going through the API
      // (rather than a direct Firestore read) is what makes the cross-tenant
      // super-user path work without tripping Firestore tenant-isolation rules.
      try {
        this.agents = await listTenantAgents({ tenantId: this.tenantId });
      } catch (err) {
        console.error('Failed to fetch agents:', err);
        this.agents = [];
      }
    },

    async fetchSources() {
      this.loading = true;
      try {
        const { data } = await axios.get(config.api_url + 'signup/sources', {
          headers: await authHeaders(),
          params: this.tenantId ? { target_tenant_id: this.tenantId } : {},
        });
        if (data.code === 'SUCCESS') {
          this.sources = (data.data || []).map(s => ({
            ...s,
            source_campaign: `${s.source || ''} ${s.campaign || ''}`.trim(),
          }));
          for (const s of this.sources) {
            const name = (s.source || '').charAt(0).toUpperCase() + (s.source || '').slice(1);
            if (name && !this.sourceOptions.includes(name)) {
              this.sourceOptions.push(name);
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch sources:', err);
      }
      this.loading = false;
    },

    async createSource() {
      if (!this.newSource.agentId || !this.newSource.source) return;
      this.creating = true;
      this.generatedLink = "";
      this.copied = false;

      const agent = this.agents.find(a => a.id === this.newSource.agentId);
      const sourceValue = this.newSource.source === '__custom__'
        ? this.newSource.customSource
        : this.newSource.source;
      if (!sourceValue) return;
      try {
        const body = {
          agent_id: this.newSource.agentId,
          agent_name: agent ? agent.label : '',
          source: sourceValue.toLowerCase(),
          campaign: this.newSource.campaign,
        };
        if (this.tenantId) body.target_tenant_id = this.tenantId;
        const { data } = await axios.post(config.api_url + 'signup/create-source', body, {
          headers: await authHeaders(),
        });

        if (data.code === 'SUCCESS') {
          this.generatedLink = data.data.url;
          this.copyText(data.data.url);
          this.copied = true;
          setTimeout(() => { this.copied = false; }, 1500);
          await this.fetchSources();
        }
      } catch (err) {
        console.error('Failed to create source:', err);
      }
      this.creating = false;
    },

    copyLink() {
      this.copyText(this.generatedLink);
      this.copied = true;
      setTimeout(() => { this.copied = false; }, 2000);
    },

    copyText(text) {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text);
      } else {
        const el = document.createElement('textarea');
        el.value = text;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
    },

    async updateReach(item) {
      const reach = parseInt(item.reach) || 0;
      try {
        const body = { source_id: item.id, reach };
        if (this.tenantId) body.target_tenant_id = this.tenantId;
        await axios.post(config.api_url + 'signup/update-source', body, {
          headers: await authHeaders(),
        });
        this.$set(item, '_reachSaved', true);
        setTimeout(() => { this.$set(item, '_reachSaved', false); }, 2000);
      } catch (err) {
        console.error('Failed to update reach:', err);
      }
    },

    conversionColor(signups, reach) {
      if (!reach || reach === 0) return '#6B7280';
      if (signups === 0) return '#6B7280';
      const rate = (signups / reach) * 100;
      if (rate >= 5) return '#2E7D32';
      if (rate >= 2) return '#F57F17';
      return '#C62828';
    },

    downloadQR(url, elementId) {
      const canvas = document.getElementById(elementId);
      if (!canvas) return;
      const canvasEl = canvas.tagName === 'CANVAS' ? canvas : canvas.querySelector('canvas');
      if (!canvasEl) return;
      const link = document.createElement('a');
      link.download = `kiey-qr-${Date.now()}.png`;
      link.href = canvasEl.toDataURL('image/png');
      link.click();
    },

    downloadSignups(item) {
      if (!item.signups || !item.signups.length) return;
      const headers = ['Name', 'Role', 'Phone', 'Email', 'Status', 'Date'];
      const rows = item.signups.map(s => [
        `${s.first_name || ''} ${s.last_name || ''}`.trim(),
        s.role || '',
        s.phone_number || '',
        s.email || '',
        s.status || '',
        this.formatDate(s.created_at),
      ]);
      const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const link = document.createElement('a');
      link.download = `kiey-signups-${item.source}-${Date.now()}.csv`;
      link.href = URL.createObjectURL(blob);
      link.click();
      URL.revokeObjectURL(link.href);
    },

    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

.ss-page {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ── Cards ───────────────────────────────────────────── */
.ss-card {
  background: #FFFFFF;
  border: 1px solid #E6ECF5;
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 12px;
}

.ss-card__title {
  font-size: 15px;
  font-weight: 700;
  color: #0C2454;
  margin: 0;
}

.ss-card__desc {
  font-size: 13px;
  color: #6B7280;
  margin: 2px 0 20px;
}

.ss-form {
  margin-top: 0;
}

/* ── Form ────────────────────────────────────────────── */
.ss-form__grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 140px;
  gap: 8px;
  align-items: end;
}

.ss-form__label {
  display: block;
  font-size: 11px !important;
  font-weight: 600 !important;
  color: #344054 !important;
  margin-bottom: 4px;
  margin-top: 0;
  padding-top: 0;
  line-height: 1 !important;
  /* Neutralize global app.scss `label { transform: translateY(-18px);
     font-size: 15px; ... !important }` (Vuetify floating-label rule). */
  transform: none !important;
  max-width: 100% !important;
}

.ss-form__field--btn {
  min-width: 140px;
}

.ss-generate-btn {
  width: 100%;
  height: 36px;
  border-radius: 8px;
  background: #2F6FED;
  color: #FFFFFF;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 120ms ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ss-generate-btn:hover:not(:disabled) {
  background: #2660D4;
}

.ss-generate-btn--disabled {
  background: #AFC2EB;
  cursor: not-allowed;
}

.ss-form__optional {
  font-weight: 400;
  color: #98A2B3;
}

/* ── Result (compact row) ────────────────────────────── */
.ss-result-row {
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #FFFFFF;
  border-radius: 8px;
  border: 1px solid #E6ECF5;
  height: 56px;
}

.ss-result-row__left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.ss-result-row__url {
  font-size: 13px;
  color: #2F6FED;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ss-result-row__right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.ss-result-row__qr {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ss-result-row__dl {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 120ms ease;
}

.ss-result-row__dl:hover { background: #F0F3F8; }

.ss-result-row__copy {
  height: 28px;
  padding: 0 10px;
  border-radius: 6px;
  background: #2F6FED;
  color: white;
  font-size: 13px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: background 120ms ease;
  font-family: 'Inter', sans-serif;
  white-space: nowrap;
}

.ss-result-row__copy:hover { background: #2660D4; }

/* ── Table header ────────────────────────────────────── */
.ss-table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.ss-table-header__left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ss-badge {
  background: #EEF4FF;
  color: #2F6FED;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.ss-table-header__search {
  position: relative;
}

.ss-search-input {
  height: 36px;
  width: 180px;
  border: 1px solid #D9E1F0;
  border-radius: 8px;
  padding: 0 10px 0 30px;
  font-size: 13px;
  color: #14213D;
  outline: none;
  transition: border-color 120ms ease;
  font-family: 'Inter', sans-serif;
}

.ss-search-input:focus {
  border-color: #2F6FED;
  box-shadow: 0 0 0 3px rgba(47, 111, 237, 0.08);
}

.ss-search-input::placeholder { color: #A0AEC0; }

/* ── Table cells ─────────────────────────────────────── */
.ss-cell-bold {
  font-weight: 600;
  color: #14213D;
  font-size: 13px;
}

.ss-cell-muted {
  color: #98A2B3;
  font-size: 13px;
}

.ss-cell-link {
  font-size: 12px;
  color: #2F6FED;
  white-space: nowrap;
  text-decoration: none;
}

.ss-cell-link:hover { text-decoration: underline; }

.ss-icon-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  transition: background 120ms ease;
}

.ss-icon-btn:hover { background: #EEF4FF; }

.ss-qr-cell {
  cursor: pointer;
  padding: 3px;
  border-radius: 6px;
  transition: background 120ms ease;
  display: inline-flex;
}

.ss-qr-cell:hover { background: #EEF4FF; }

.ss-signups {
  font-size: 14px;
  font-weight: 700;
  color: #C0C7D4;
}

.ss-signups--active { color: #0C2454; }

.ss-reach-input {
  width: 60px;
  height: 28px;
  border: 1px solid #E6ECF5;
  border-radius: 6px;
  padding: 0 8px;
  font-size: 13px;
  font-weight: 500;
  color: #14213D;
  text-align: right;
  outline: none;
  transition: border-color 120ms ease;
  font-family: 'Inter', sans-serif;
}

.ss-reach-input:focus {
  border-color: #2F6FED;
  box-shadow: 0 0 0 2px rgba(47, 111, 237, 0.10);
}

.ss-reach-input::-webkit-inner-spin-button,
.ss-reach-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.ss-conversion {
  font-size: 13px;
  font-weight: 700;
}

/* ── Expanded row ────────────────────────────────────── */
.ss-expanded {
  padding: 16px 24px;
  background: #FAFBFE;
  border-top: 1px solid #EEF2F7;
}

.ss-expanded__title {
  font-weight: 600;
  font-size: 13px;
  color: #0C2454;
  margin-bottom: 10px;
}

.ss-expanded__table {
  width: 100%;
  font-size: 12px;
  border-collapse: collapse;
}

.ss-expanded__table th {
  color: #667085;
  text-align: left;
  padding: 5px 10px;
  font-weight: 500;
  border-bottom: 1px solid #EEF2F7;
}

.ss-expanded__table td {
  padding: 6px 10px;
  border-top: 1px solid #EEF2F7;
  color: #344054;
}

.ss-download-btn {
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  color: #2F6FED;
  background: none;
  border: 1px solid #D6E4FF;
  border-radius: 6px;
  padding: 4px 10px;
  cursor: pointer;
  transition: all 120ms ease;
  font-family: 'Inter', sans-serif;
}

.ss-download-btn:hover {
  background: #EEF4FF;
}

.ss-expanded__empty {
  color: #98A2B3;
  font-size: 13px;
}

/* ── Data table overrides ────────────────────────────── */
.ss-data-table >>> .v-data-table__wrapper {
  font-size: 13px;
  overflow-x: auto;
}

.ss-data-table >>> .v-data-table__wrapper table {
  min-width: 800px;
}

.ss-data-table >>> .v-data-table__wrapper table tbody tr {
  height: 44px;
}

.ss-data-table >>> .v-data-table__wrapper table tbody tr:hover {
  background: #F8FBFF !important;
}

.ss-data-table >>> .v-data-table__wrapper table thead th {
  font-size: 10px;
  font-weight: 600;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 6px 10px;
}

.ss-data-table >>> .v-data-table__wrapper table tbody td {
  padding: 4px 10px;
}

.ss-data-table ::v-deep .v-data-table__expand-icon {
  font-size: 14px !important;
  color: #A0AEC0 !important;
}

.ss-data-table ::v-deep .v-data-table__expand-icon .v-icon {
  font-size: 14px !important;
}

.ss-data-table >>> .v-data-footer {
  font-size: 11px;
  color: #98A2B3;
}

/* ── Native input styles ─────────────────────────────── */
.ss-input {
  width: 100%;
  height: 36px;
  border: 1.5px solid #D9E1F0;
  border-radius: 8px;
  padding: 0 10px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: #14213D;
  outline: none;
  transition: border-color 120ms ease;
  background: #FFFFFF;
  box-sizing: border-box;
}

.ss-input::placeholder { color: #A0AEC0; }

.ss-input:focus {
  border-color: #2F6FED;
  box-shadow: 0 0 0 2px rgba(47, 111, 237, 0.08);
}

.ss-select {
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%2398A2B3' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  padding-right: 28px;
  cursor: pointer;
}

.ss-select:invalid,
.ss-select option[value=""][disabled] {
  color: #A0AEC0;
}

.ss-custom-source {
  display: flex;
  gap: 6px;
  align-items: center;
}

.ss-custom-source__input {
  flex: 1;
  min-width: 0;
}

.ss-custom-source__cancel {
  width: 28px;
  height: 36px;
  flex-shrink: 0;
  border: 1.5px solid #D9E1F0;
  border-radius: 8px;
  background: #FFFFFF;
  color: #667085;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 120ms ease;
}

.ss-custom-source__cancel:hover {
  border-color: #C62828;
  color: #C62828;
  background: #FFF5F5;
}

@media (max-width: 900px) {
  .ss-form__grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .ss-form__field--btn {
    grid-column: 1;
  }

  .ss-table-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}
</style>
